import { useEffect, useState, type FC } from 'react';
import { createPortal } from 'react-dom';

import { KICKOFF_VIDEO } from '@/app/agent/media.ts';

const usePrefersReducedMotion = (): boolean => {
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(query.matches);
    const onChange = (event: MediaQueryListEvent): void => setReducedMotion(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  return reducedMotion;
};

/**
 * The owner's showreel as the backdrop of the whole stage — behind the header,
 * the content column and the dock, filling the window rather than sitting in a
 * card.
 *
 * The layer is portaled into the transform-free screen wrapper that owns the
 * kickoff cards and sits at z-index 0. The cards are z-index 10 siblings in
 * that same stacking context, so native `backdrop-filter` samples this actual
 * video. There is deliberately no clipped or duplicated card video here.
 *
 * It is mounted BY a screen, so the footage belongs to that step: when the
 * visitor moves on, the layer unmounts, the video stops downloading and
 * decoding, and the site returns to its flat dark ground.
 *
 * The clip carries an audio track, so it is always muted — autoplay requires it
 * and a soundtrack nobody asked for is hostile. A visitor who prefers reduced
 * motion gets a still first frame instead of movement.
 *
 * The scrim is what makes interface on top of moving footage readable: the clip
 * swings from bright sky to dark water, and text over raw video fails the
 * moment the scene brightens. Raw black/white opacities are correct here —
 * their job is to darken footage, not to follow the theme.
 */
export const StageVideoBackdrop: FC = () => {
  const reducedMotion = usePrefersReducedMotion();
  const [stageRoot, setStageRoot] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setStageRoot(
      document.querySelector<HTMLElement>('.stage [data-stage-page] > div > div.animate-fadeUp'),
    );
  }, []);

  if (!stageRoot) {
    return null;
  }

  return createPortal(
    <div className="pointer-events-none fixed inset-0 z-0 overflow-hidden" aria-hidden="true">
      <video
        src={KICKOFF_VIDEO}
        className="h-full w-full object-cover"
        autoPlay={!reducedMotion}
        loop
        muted
        playsInline
        preload="metadata"
        tabIndex={-1}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/70" />
    </div>,
    stageRoot,
  );
};
